import { PublicNodeDTO, PublicResourceDTO } from "../../../types/api/cmsPublic";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { DATA_CARD_ONE_BANNER } from "../../../assets";
import {
  HOME,
  STATISTICS,
  resourceDetailPath,
} from "../../../constants/routes";
import { CustomBreadcrumb, ErrorMessage, Loader } from "../../moleculas";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CmsPublicApiClient, {
  CmsApiError,
} from "../../../services/api/cmsPublicApiClient";
import {
  mapNodeToCardVM,
  mapOrganizationToVM,
  mapResourceDetailToVM,
} from "../../../services/mappers/cmsPublicMappers";
import GeoenvironmentalSection from "../About/GeoenvironmentalSection";
import CategoryGrid from "./categoryGrid";
import {
  OrganizationNodesVM,
  ResourceDetailVM,
} from "../../../types/viewModels/cmsPublic";
import { formatDate } from "../../../utilities/formatDate";

const STATIC_IMAGE_DEFAULT = DATA_CARD_ONE_BANNER;
const service = new CmsPublicApiClient();
const cleanSubtitle = (value: string) => value.replace(/^\(+|\)+$/g, "").trim();

const buildErrorMessage = (error: unknown) => {
  if (error instanceof CmsApiError) {
    if (error.status === 404) {
      return "El recurso solicitado no existe o no está publicado.";
    }

    if (error.status === 400 || error.status === 422) {
      return "El identificador del recurso no es válido.";
    }

    return "No fue posible cargar el detalle del recurso desde CMS.";
  }

  return "Ocurrió un error inesperado al cargar el recurso.";
};

const CardDetailDescription = () => {
  const { t } = useTranslation("statistics");
  const { id, resourceId } = useParams();
  const resourceRoute = resourceId !== undefined;
  const generation = useRef(0);
  const [node, setNode] = useState<PublicNodeDTO | null>(null);
  const [nodeResources, setNodeResources] = useState<PublicResourceDTO[]>([]);
  const [ancestors, setAncestors] = useState<
    Array<{ name: string; href: string }>
  >([]);
  const [nodeChildren, setNodeChildren] = useState<PublicNodeDTO[]>([]);
  const slug = useMemo(() => resourceId || id || "", [id, resourceId]);

  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState<OrganizationNodesVM | null>(
    null
  );
  const [resource, setResource] = useState<ResourceDetailVM | null>(null);
  const [error, setError] = useState("");
  const [nodeOpenError, setNodeOpenError] = useState("");

  const resolveResourceLink = (resource: any) => {
    const externalUrl = resource?.visualizations?.[0]?.externalUrl;
    if (externalUrl) {
      return externalUrl;
    }

    const embedUrl = resource?.visualizations?.[0]?.embedUrl;
    if (embedUrl) {
      return embedUrl;
    }

    const marcoNormativoUrl = resource?.marcoNormativo?.url;
    if (marcoNormativoUrl) {
      return marcoNormativoUrl;
    }

    return "";
  };

  const fetchDetail = useCallback(async () => {
    const request = ++generation.current;
    const stale = () => request !== generation.current;
    if (!slug) {
      setOrganization(null);
      setResource(null);
      setError("No se indicó el recurso a consultar.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setNode(null);
    setOrganization(null);
    setResource(null);
    setNodeResources([]);
    setAncestors([]);
    setNodeChildren([]);
    setError("");
    setNodeOpenError("");

    try {
      if (!resourceRoute) {
        const organizationResponse = await service.getOrganizations(1, 100);
        if (stale()) return;
        const match = organizationResponse.data.find(
          (item) => item.slug === slug
        );

        if (match) {
          const mappedOrganization = mapOrganizationToVM(match);
          const tree = await service.getOrganizationTree(match.slug);
          if (stale()) return;
          const filteredNodes = tree.nodos.map(mapNodeToCardVM);
          setNodeChildren(tree.nodos);

          const organizationNodes =
            filteredNodes.length > 0
              ? filteredNodes
              : mappedOrganization.nodes.length > 0
              ? mappedOrganization.nodes
              : [];

          setOrganization({
            ...mappedOrganization,
            nodes: organizationNodes,
          });
          setResource(null);
          return;
        }

        try {
          const { data: foundNode } = await service.getNodeBySlug(slug);
          if (stale()) return;
          setNode(foundNode);
          setOrganization(null);
          setResource(null);
          if (foundNode.organizacion) {
            const tree = await service.getOrganizationTree(
              foundNode.organizacion.slug
            );
            if (stale()) return;
            const flatten = (nodes: PublicNodeDTO[]): PublicNodeDTO[] =>
              nodes.flatMap((n) => [n, ...flatten(n.hijos || [])]);
            const all = flatten(tree.nodos);
            const current = all.find((n) => n.slug === slug);
            setNodeChildren(current?.hijos || []);
            const path = (foundNode.rutaIdentificadores || slug).split("/");
            setAncestors([
              {
                name: tree.organizacion.name,
                href: `/datos/acerca/${encodeURIComponent(
                  tree.organizacion.slug
                )}`,
              },
              ...path.slice(0, -1).map((part) => ({
                name: all.find((n) => n.slug === part)?.name || part,
                href: `/datos/acerca/${encodeURIComponent(part)}`,
              })),
            ]);
          } else setNodeChildren(foundNode.hijos || []);
          if (!foundNode.tieneHijos) {
            const resources: PublicResourceDTO[] = [];
            let page = 1;
            let response;
            do {
              response = await service.getResources({
                node: slug,
                page: page++,
                pageSize: 100,
              });
              resources.push(...response.data);
            } while (
              response.data.length &&
              resources.length < response.meta.pagination.total
            );
            if (stale()) return;
            setNodeResources(resources);
          }
          return;
        } catch (nodeError) {
          if (!(nodeError instanceof CmsApiError) || nodeError.status !== 404)
            throw nodeError;
        }
      }
      const resourceResponse = await service.getResourceBySlug(slug);
      if (stale()) return;
      if (resourceResponse.data.node?.slug) {
        try {
          const { data: owner } = await service.getNodeBySlug(
            resourceResponse.data.node.slug
          );
          if (owner.organizacion) {
            const tree = await service.getOrganizationTree(
              owner.organizacion.slug
            );
            if (stale()) return;
            const flatten = (nodes: PublicNodeDTO[]): PublicNodeDTO[] =>
              nodes.flatMap((n) => [n, ...flatten(n.hijos || [])]);
            const all = flatten(tree.nodos);
            setAncestors([
              {
                name: tree.organizacion.name,
                href: `/datos/acerca/${encodeURIComponent(
                  tree.organizacion.slug
                )}`,
              },
              ...(owner.rutaIdentificadores || owner.slug)
                .split("/")
                .map((part) => ({
                  name: all.find((n) => n.slug === part)?.name || part,
                  href: `/datos/acerca/${encodeURIComponent(part)}`,
                })),
            ]);
          }
        } catch {
          // La disponibilidad del recurso no depende de una segunda consulta para las migas.
        }
      }
      if (stale()) return;
      setResource(mapResourceDetailToVM(resourceResponse.data));
      setOrganization(null);
    } catch (requestError) {
      if (stale()) return;
      setOrganization(null);
      setResource(null);
      setError(buildErrorMessage(requestError));
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [slug, resourceRoute]);

  useEffect(() => {
    fetchDetail();
    return () => {
      generation.current++;
    };
  }, [fetchDetail]);

  const openNodeResource = useCallback(async (nodeSlug: string) => {
    setNodeOpenError("");

    try {
      const response = await service.getResources({
        node: nodeSlug,
        page: 1,
        pageSize: 1,
      });

      const firstResource = response.data?.[0];
      if (!firstResource?.slug) {
        setNodeOpenError("Este nodo no tiene recursos publicados.");
        return;
      }

      const detailResponse = await service.getResourceBySlug(
        firstResource.slug
      );
      const directLink = resolveResourceLink(detailResponse.data);

      if (directLink) {
        window.location.href = directLink;
        return;
      }

      window.location.href = resourceDetailPath(firstResource.slug);
    } catch {
      setNodeOpenError("No fue posible abrir el recurso asociado al nodo.");
    }
  }, []);

  return (
    <div className="w-100 d-flex justify-content-center flex-column align-items-center pb-5 card-detail">
      <div className="card-detail-breadcrumb">
        <CustomBreadcrumb
          isWhite={false}
          breadcrumbs={[
            { name: t("home"), href: HOME },
            { name: t("title"), href: STATISTICS },
            ...ancestors,
            {
              name:
                node?.name ||
                organization?.name ||
                resource?.title ||
                "Detalle",
              href: "",
            },
          ]}
        />
      </div>
      <div
        style={{ overflow: "hidden" }}
        className="card-detail-background d-flex w-100 justify-content-end"
      >
        <img src={STATIC_IMAGE_DEFAULT} alt="statistics-banner" />
      </div>

      <div className="w-75 mb-5 d-flex flex-column align-items-center card-detail-text">
        <div className="w-100 align-items-left">
          {loading ? <Loader /> : null}

          {!loading && error ? (
            <div className="d-flex flex-column align-items-center gap-3 my-4">
              <ErrorMessage message={error} />
              <Button onClick={fetchDetail} className="custom-primary-button">
                Reintentar
              </Button>
            </div>
          ) : null}

          {!loading && !error && !resource && !organization && !node ? (
            <ErrorMessage message="No se encontró información para este detalle." />
          ) : null}

          {!loading && !error && node ? (
            <section>
              <h1>{node.name}</h1>
              {nodeChildren.length ? (
                <CategoryGrid
                  items={nodeChildren.map((child) => ({
                    title: child.name,
                    description: child.subtitulo || "",
                    iconSrc: child.icono || "",
                    href: `/datos/acerca/${encodeURIComponent(child.slug)}`,
                  }))}
                />
              ) : nodeResources.length ? (
                <ul>
                  {nodeResources.map((item) => (
                    <li key={item.slug}>
                      <a href={resourceDetailPath(item.slug)}>{item.title}</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Este nodo no tiene recursos publicados.</p>
              )}
            </section>
          ) : null}
          {!loading && !error && organization ? (
            <div className="d-flex flex-column col-sm col-md-12 mb-2">
              <h1>
                {organization.name}
                {organization.subtitle ? (
                  <>
                    <br />({cleanSubtitle(organization.subtitle)})
                  </>
                ) : null}
              </h1>
              <hr className="custom-divider" />

              {organization.description ? (
                <div className="col-lg-5 col-12 mb-4">
                  {organization.description}
                </div>
              ) : null}

              <br />
              {nodeOpenError ? <ErrorMessage message={nodeOpenError} /> : null}
              {organization.nodes.length === 0 ? (
                <ErrorMessage message="Esta organización no tiene nodos publicados." />
              ) : (
                <CategoryGrid
                  items={organization.nodes.map((node) => ({
                    title: node.title,
                    description: node.description,
                    iconSrc: node.iconSrc,
                    iconAlt: node.title,
                    href: nodeChildren.find((n) => n.slug === node.slug)
                      ?.tieneHijos
                      ? `/datos/acerca/${encodeURIComponent(node.slug)}`
                      : undefined,
                    onClick: nodeChildren.find((n) => n.slug === node.slug)
                      ?.tieneHijos
                      ? undefined
                      : () => openNodeResource(node.slug),
                  }))}
                />
              )}
            </div>
          ) : null}

          {!loading && !error && resource ? (
            <div className="d-flex flex-column col-sm col-md-12 mb-2">
              <h1>{resource.title}</h1>
              <hr className="custom-divider" />

              {resource.description ? (
                <div className="col-lg-5 col-12 mb-2">
                  {resource.description}
                </div>
              ) : null}

              {resource.nodeName ? (
                <p className="mb-1">
                  <strong>Nodo:</strong> {resource.nodeName}
                </p>
              ) : null}

              {resource.publishedAt ? (
                <p className="mb-1">
                  <strong>Publicado:</strong> {formatDate(resource.publishedAt)}
                </p>
              ) : null}

              {resource.tags.length > 0 ? (
                <p className="mb-3">
                  <strong>Etiquetas:</strong> {resource.tags.join(", ")}
                </p>
              ) : null}

              {resource.marcoNormativoUrl ? (
                <div className="mb-4">
                  <a
                    className="custom-primary-button btn text-decoration-none"
                    href={resource.marcoNormativoUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {resource.marcoNormativoName || "Descargar marco normativo"}
                  </a>
                </div>
              ) : null}

              <h3 className="mb-3">Visualizaciones</h3>
              {resource.visualizations.length === 0 ? (
                <ErrorMessage message="Este recurso no tiene visualizaciones asociadas." />
              ) : null}

              {resource.visualizations.map((visualization, index) => (
                <div
                  key={`${visualization.datasetId}-${index}`}
                  className="mb-4"
                >
                  <p className="mb-2">
                    <strong>Proveedor:</strong> {visualization.provider}
                  </p>

                  {visualization.externalUrl ? (
                    <a
                      href={visualization.externalUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="d-block mb-2"
                    >
                      Abrir visualización
                    </a>
                  ) : null}

                  {visualization.embedUrl ? (
                    <iframe
                      title={`embed-${visualization.datasetId || index}`}
                      src={visualization.embedUrl}
                      width="100%"
                      height="420"
                      style={{ border: 0 }}
                      loading="lazy"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="w-100 d-flex flex-column align-items-center background-color mt-5 mt-sm-5">
        <div className="w-75 mt-5">
          <GeoenvironmentalSection />
        </div>
      </div>
    </div>
  );
};

export default CardDetailDescription;
